//* Public Constants
export const SESSION_STATUS = {
  ACTIVE: 'active',
  BLOCKED: 'blocked',
  NO_ROLE: 'no_role',
} as const;

export type SessionStatus = (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS];

export const SESSION_FAILURE = {
  UNAUTHORIZED: 'unauthorized',
  BLOCKED: 'blocked',
  NOT_PROVISIONED: 'not_provisioned',
  UNAVAILABLE: 'unavailable',
} as const;

export type SessionFailure = (typeof SESSION_FAILURE)[keyof typeof SESSION_FAILURE];

export const APP_PERMISSION = {
  READ_USERS: 'read:users',
  WRITE_USERS: 'write:users',
  READ_ROLES: 'read:roles',
  WRITE_ROLES: 'write:roles',
  READ_DELIVERY_NONCONFORMITY_TYPES: 'read:delivery-nonconformity-types',
  WRITE_DELIVERY_NONCONFORMITY_TYPES: 'write:delivery-nonconformity-types',
  READ_INCIDENTS: 'read:incidents',
  WRITE_INCIDENTS: 'write:incidents',
  READ_INCIDENTS_ALL: 'read:incidents:all',
  READ_NOTIFICATIONS: 'read:notifications',
  READ_TELEGRAM: 'read:telegram',
  READ_WAREHOUSES: 'read:warehouses',
  READ_OUTPUT_WAREHOUSES: 'read:output-warehouses',
  WRITE_OUTPUT_WAREHOUSES: 'write:output-warehouses',
  WRITE_SCHEDULES_START_ROUTE: 'write:schedules:start-route',
  WRITE_SCHEDULES_ADD_ORDERS: 'write:schedules:add-orders',
  WRITE_SCHEDULES_ADD_ORDERS_IN_ROUTE: 'write:schedules:add-orders-in-route',
  WRITE_SCHEDULES_REGISTER_ARRIVAL: 'write:schedules:register-arrival',
  WRITE_SCHEDULES_ASSIGN_VEHICLE: 'write:schedules:assign-vehicle',
  WRITE_SCHEDULES_CANCEL: 'write:schedules:cancel',
  WRITE_DELIVERIES_ON_BEHALF: 'write:deliveries:on-behalf',
  READ_NONCONFORMITIES: 'read:nonconformities',
  READ_VEHICLES: 'read:vehicles',
  WRITE_VEHICLES_CREATE: 'write:vehicles:create',
  WRITE_VEHICLES_UPDATE: 'write:vehicles:update',
  WRITE_VEHICLES_DELETE: 'write:vehicles:delete',
  READ_AREAS: 'read:areas',
  WRITE_AREAS: 'write:areas',
  READ_GATE_LOG: 'read:gate-log',
} as const;

export type AppPermission = (typeof APP_PERMISSION)[keyof typeof APP_PERMISSION];

export const APP_MODULE = {
  DASHBOARD: 'dashboard',
  DELIVERIES: 'deliveries',
  SCHEDULES: 'schedules',
  INCIDENTS: 'incidents',
  NOTIFICATIONS: 'notifications',
  USERS: 'users',
  ROLES: 'roles',
  PERMISSIONS: 'permissions',
  WAREHOUSES: 'warehouses',
  DELIVERY_NONCONFORMITY_TYPES: 'delivery-nonconformity-types',
  TELEGRAM: 'telegram',
  OUTPUT_WAREHOUSES: 'output-warehouses',
  VEHICLES: 'vehicles',
  AREAS: 'areas',
  GATE_LOG: 'gate-log',
} as const;

export type AppModule = (typeof APP_MODULE)[keyof typeof APP_MODULE];

export const APP_MODULE_LABEL: Record<AppModule, string> = {
  [APP_MODULE.DASHBOARD]: 'Dashboard',
  [APP_MODULE.DELIVERIES]: 'Entregas',
  [APP_MODULE.SCHEDULES]: 'Cédulas',
  [APP_MODULE.INCIDENTS]: 'Incidencias',
  [APP_MODULE.NOTIFICATIONS]: 'Notificaciones',
  [APP_MODULE.USERS]: 'Usuarios',
  [APP_MODULE.ROLES]: 'Roles',
  [APP_MODULE.PERMISSIONS]: 'Permisos',
  [APP_MODULE.WAREHOUSES]: 'Almacenes',
  [APP_MODULE.DELIVERY_NONCONFORMITY_TYPES]: 'Tipos de no conforme',
  [APP_MODULE.TELEGRAM]: 'Telegram',
  [APP_MODULE.OUTPUT_WAREHOUSES]: 'Almacenes de salida',
  [APP_MODULE.VEHICLES]: 'Vehículos',
  [APP_MODULE.AREAS]: 'Áreas',
  [APP_MODULE.GATE_LOG]: 'Bitácora de caseta',
};

export const MODULE_ACTION = {
  READ: 'Read',
  WRITE: 'Write',
  UPDATE: 'Update',
  DELETE: 'Delete',
} as const;

export type ModuleAction = (typeof MODULE_ACTION)[keyof typeof MODULE_ACTION];

export function isAppModule(value: unknown): value is AppModule {
  return typeof value === 'string' && Object.values(APP_MODULE).includes(value as AppModule);
}

export function isModuleAction(value: unknown): value is ModuleAction {
  return typeof value === 'string' && Object.values(MODULE_ACTION).includes(value as ModuleAction);
}

//* Public Interfaces
export interface SessionRole {
  id: string | null;
  name: string;
  description: string;
}

export interface ModuleAccess {
  code: AppModule;
  actions: ModuleAction[];
}

export interface SessionWarehouse {
  code: string;
  name: string;
}

export interface AppSession {
  subject: string;
  username: string;
  email: string;
  displayName: string;
  fullName: string;
  status: SessionStatus;
  hasWebAccess: boolean;
  role: SessionRole | null;
  permissions: string[];
  modules: ModuleAccess[];
  profileskore: string | null;
  lastLoginAt: string | null;
  warehouse: SessionWarehouse | null;
}

export interface SessionLoadResult {
  session: AppSession | null;
  failure: SessionFailure | null;
}
